import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/consolidated.css';
import '../styles/discover-header-footer.css';

interface Banner {
  image_path: string;
  title?: string;
  link?: string;
}

const Discover: React.FC = () => {
  const [currentAdSlide, setCurrentAdSlide] = useState(0);
  const [menuActive, setMenuActive] = useState(false);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const [banners, setBanners] = useState<Banner[]>([]);
  const [bannersLoaded, setBannersLoaded] = useState(false);
  const [bannersError, setBannersError] = useState(false);
  
  // Fetch ad banners
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await fetch('https://fauconbleu.ma/api/pwa/banners/website');
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();
        setBanners(data);
        setBannersLoaded(true);
      } catch (error) {
        console.error('Error fetching banners:', error);
        setBannersError(true);
        setBannersLoaded(true);
      }
    };
    
    fetchBanners();
  }, []);
  
  // Handle header scroll behavior
  useEffect(() => {
    let lastScrollY = window.scrollY;
    let scrollingDown = false;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > 70) {
        setHeaderScrolled(true);
        
        if (currentScrollY > lastScrollY && !scrollingDown) {
          scrollingDown = true;
          setHeaderHidden(true);
        } else if (currentScrollY < lastScrollY && scrollingDown) {
          scrollingDown = false;
          setHeaderHidden(false);
        }
      } else {
        setHeaderScrolled(false);
      }
      
      lastScrollY = currentScrollY;
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Auto-rotate ad slides if there are banners
  useEffect(() => {
    if (banners.length > 1) {
      const interval = setInterval(() => {
        setCurrentAdSlide((prev) => (prev + 1) % banners.length);
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [banners]);
  
  // Handle menu toggle
  const toggleMenu = () => {
    setMenuActive(!menuActive);
  };
  
  // Handle ad banner controls
  const handleAdSlideChange = (index: number) => {
    setCurrentAdSlide(index);
  };
  
  const nextAdSlide = () => {
    if (banners.length > 0) {
      setCurrentAdSlide((prev) => (prev + 1) % banners.length);
    }
  };
  
  const prevAdSlide = () => {
    if (banners.length > 0) {
      setCurrentAdSlide((prev) => (prev - 1 + banners.length) % banners.length);
    }
  };

  // Render banner section only if banners are available
  const renderAdBannerSection = () => {
    if (!bannersLoaded || banners.length === 0 || bannersError) {
      return null;
    }
    
    return (
      <section className="ad-banners-section">
        <div className="ad-carousel-container">
          <div className="ad-carousel-track" style={{ transform: `translateX(-${currentAdSlide * 100}%)` }}>
            {banners.map((banner, index) => (
              <div 
                key={index} 
                className="ad-banner-item"
                onClick={() => banner.link && window.open(banner.link, '_blank')}
                style={{ cursor: banner.link ? 'pointer' : 'default' }}
              >
                <img 
                  src={`https://fauconbleu.ma/storage/${banner.image_path}`} 
                  alt={banner.title || `Promotion ${index + 1}`} 
                  className="ad-banner-img" 
                />
              </div>
            ))}
          </div>
          {banners.length > 1 && (
            <>
              <div className="ad-carousel-indicators">
                {banners.map((_, index) => (
                  <span 
                    key={index}
                    className={`ad-indicator ${currentAdSlide === index ? 'active' : ''}`}
                    onClick={() => handleAdSlideChange(index)}
                  ></span>
                ))}
              </div>
              <button className="ad-carousel-btn prev" onClick={prevAdSlide}>
                <i className="fas fa-chevron-left"></i>
              </button>
              <button className="ad-carousel-btn next" onClick={nextAdSlide}>
                <i className="fas fa-chevron-right"></i>
              </button>
            </>
          )}
        </div>
      </section>
    );
  };

  return (
    <>
      <header className={`main-header ${headerScrolled ? 'scrolled' : ''} ${headerHidden ? 'hidden' : ''}`}>
        <div className="header-container">
          <Link to="/" className="logo-container">
            <img src="/images/logo faucon bleu.png" alt="Faucon Bleu Logo" className="logo" />
          </Link>
          <nav className="main-nav">
            <button 
              className={`menu-toggle ${menuActive ? 'active' : ''}`}
              aria-label="Toggle Menu"
              onClick={toggleMenu}
            >
              <span className="bar" style={menuActive ? {transform: 'rotate(-45deg) translate(-5px, 6px)'} : {}}></span>
              <span className="bar" style={menuActive ? {opacity: '0'} : {}}></span>
              <span className="bar" style={menuActive ? {transform: 'rotate(45deg) translate(-5px, -6px)'} : {}}></span>
            </button>
            <ul className={`nav-menu ${menuActive ? 'active' : ''}`}>
              <li><Link to="/">Accueil</Link></li>
              <li><Link to="/discover" className="active">Nos Services</Link></li>
              <li><a href="#contact">Contact</a></li>
            </ul>
          </nav>
        </div>
      </header>
      
      <section className="hero-banner"></section>
      
      <div className="main-content">
        <h1>L'entretien automobile simplifié pour vous et votre famille</h1>
        <p>Chez Faucon Bleu, nous avons repensé l'entretien automobile pour le rendre plus pratique, rapide et accessible à tous.</p>
        <p>Que ce soit à domicile, sur votre lieu de travail, ou dans notre atelier situé à Bouskoura, nous mettons à votre disposition des solutions innovantes et personnalisées.</p>
      </div>
      
      <section className="partners-section">
        <h2>Nos Partenaires</h2>
        
        <div className="carousel-container">
          <div className="carousel-track">
            {/* First set of partners */}
            <div className="partner-item">
              <img src="/images/8.png" alt="Shell" className="partner-logo" />
            </div>
            <div className="partner-item">
              <img src="/images/9.png" alt="Sanlam" className="partner-logo" />
            </div>
            <div className="partner-item">
              <img src="/images/10.png" alt="Goodyear" className="partner-logo" />
            </div>
            <div className="partner-item">
              <img src="/images/11.png" alt="Bosh" className="partner-logo" />
            </div>
            <div className="partner-item">
              <img src="/images/12.png" alt="Bridgston" className="partner-logo" />
            </div>
            
            {/* Duplicate for infinite scroll effect */}
            <div className="partner-item">
              <img src="/images/8.png" alt="Shell" className="partner-logo" />
            </div>
            <div className="partner-item">
              <img src="/images/9.png" alt="Sanlam" className="partner-logo" />
            </div>
            <div className="partner-item">
              <img src="/images/10.png" alt="Goodyear" className="partner-logo" />
            </div>
            <div className="partner-item">
              <img src="/images/11.png" alt="Bosh" className="partner-logo" />
            </div>
            <div className="partner-item">
              <img src="/images/12.png" alt="Bridgston" className="partner-logo" />
            </div>
          </div>
        </div>
      </section>
      
      <section className="services-section">
        <h2>Nos Services pour Particuliers</h2>
        
        <div className="services-container">
          {/* Ateliers Mobiles */}
          <div className="service-item">
            <div className="service-heading">Ateliers Mobiles</div>
            <div className="service-content">
              <div className="service-text">
                <p>Ateliers Mobiles : Vidanges, changements de pneus et vérifications rapides, directement à votre porte.</p>
                <Link to="/reservation" className="service-btn">Simulez votre vidange</Link>
              </div>
              <div className="service-img">
                <img src="/images/mobile-atelier.png" alt="Ateliers Mobiles" />
              </div>
            </div>
          </div>
          
          {/* Atelier à Bouskoura */}
          <div className="service-item right-aligned">
            <div className="service-heading">Atelier à Bouskoura</div>
            <div className="service-content">
              <div className="service-img">
                <img src="/images/atelier-bouskoura.png" alt="Atelier à Bouskoura" />
              </div>
              <div className="service-text">
                <p>Des équipements de pointe pour la carrosserie et les réparations mécaniques.</p>
                <a href="https://wa.me/212608112626" target="_blank" rel="noopener noreferrer" className="service-btn">Prenez rendez-vous</a>
              </div>
            </div>
          </div>
          
          {/* Café-Resto L'ATELIER */}
          <div className="service-item">
            <div className="service-heading">Café-Resto "L'ATELIER"</div>
            <div className="service-content">
              <div className="service-text">
                <p>Des équipements de pointe pour la carrosserie et les réparations mécaniques.</p>
                <a href="https://www.google.com/maps?q=33.428106,-7.642943" target="_blank" rel="noopener noreferrer" className="service-btn">Passez à L'ATELIER</a>
              </div>
              <div className="service-img">
                <img src="/images/cafe-atelier.png" alt="Café-Resto L'ATELIER" />
              </div>
            </div>
          </div>
          
          {/* Assistance */}
          <div className="service-item right-aligned">
            <div className="service-heading">Assistance</div>
            <div className="service-content">
              <div className="service-img">
                <img src="/images/assistance.png" alt="Assistance" />
              </div>
              <div className="service-text">
                <p>Contactez nous en cas de besoin et laissez nos experts s'occuper de tout.</p>
                <a href="tel:+212602094593" className="service-btn">Contactez nous</a>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {renderAdBannerSection()}
      
      <section className="b2b-section">
        <div className="b2b-container">
          <h2>Vous représentez une entreprise ?</h2>
          <p>Découvrez nos contrats B2B adaptés à vos besoins et bénéficiez de solutions sur mesure pour la gestion de votre flotte.</p>
          <a href="#" className="btn">Solutions B2B</a>
        </div>
      </section>
      
      <section className="app-section">
        <div className="app-container">
          <div className="app-text">
            <h2>L'application mobile Faucon Bleu : Vos voitures, votre contrôle</h2>
            <p>gérez l'entretien de votre voiture et de celles de toute votre famille depuis un seul endroit, facilement et efficacement.</p>
            <p className="app-subtext">Que ce soit pour planifier une vidange, suivre une intervention en temps réel ou recevoir des rappels d'entretien, tout se fait en quelques clics.</p>
          </div>
          <div className="app-image">
            <img src="/images/mobile app.png" alt="Faucon Bleu Mobile App" />
          </div>
          <div className="app-links">
            <a href="#" className="app-store-link"><img src="/images/app_store.png" alt="Download on App Store" /></a>
            <a href="#" className="play-store-link"><img src="/images/google_play.png" alt="Get it on Google Play" /></a>
          </div>
        </div>
      </section>
      
      <footer id="contact" className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <img src="/images/logo faucon bleu.png" alt="Faucon Bleu Logo" loading="lazy" />
            </div>
            <div className="footer-contact">
              <h3>Contact</h3>
              <div className="contact-info">
                <div className="contact-item">
                  <i className="fas fa-phone-alt"></i>
                  <div>
                    <p>07 02 09 45 93</p>
                    <p>06 08 11 26 26</p>
                  </div>
                </div>
                <div className="contact-item">
                  <i className="fas fa-envelope"></i>
                  <div>
                    <p><a href="mailto:contact@faucon-bleu.ma">contact@faucon-bleu.ma</a></p>
                  </div>
                </div>
                <div className="contact-item">
                  <i className="fas fa-map-marker-alt"></i>
                  <div>
                    <p>Atelier de Bouskoura</p>
                    <p>Zone Industrielle, Bouskoura</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="footer-social">
              <h3>Suivez-nous</h3>
              <div className="social-links">
                <a href="#" aria-label="Facebook"><i className="fab fa-facebook-f"></i></a>
                <a href="#" aria-label="Instagram"><i className="fab fa-instagram"></i></a>
                <a href="#" aria-label="LinkedIn"><i className="fab fa-linkedin-in"></i></a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 Faucon Bleu. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Discover;